/**
 * SiteScopeContext — current site filter for the whole SPA.
 *
 * Owner: drafts/55 v0.9 — multi-site UX layer.
 *
 * The scope is one of:
 *   - { kind: 'all' }                                 ← combined view (default)
 *   - { kind: 'site', siteId, siteCode, siteName }    ← scoped to one site
 *
 * Selection persists in localStorage so a returning user keeps their scope.
 * Every list hook reads `useSiteScope()` and appends `?site=<id>` to its
 * query so the mock-api / Spring service can filter.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

export type SiteScope =
  | { kind: 'all' }
  | { kind: 'site'; siteId: string; siteCode: string; siteName: string };

interface SiteScopeContextValue {
  scope: SiteScope;
  setScope: (s: SiteScope) => void;
  isAll: boolean;
  /** Helper: returns the active site id, or null when scope === 'all'. */
  siteId: string | null;
  /** Helper: returns the active site code, or null when scope === 'all'. */
  siteCode: string | null;
}

const STORAGE_KEY = 'edms.site-scope';
const DEFAULT_SCOPE: SiteScope = { kind: 'all' };

const SiteScopeContext = createContext<SiteScopeContextValue | null>(null);

function loadFromStorage(): SiteScope {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SCOPE;
    const parsed = JSON.parse(raw);
    if (parsed?.kind === 'site' && parsed.siteId) return parsed as SiteScope;
    return DEFAULT_SCOPE;
  } catch {
    return DEFAULT_SCOPE;
  }
}

export function SiteScopeProvider({ children }: { children: ReactNode }) {
  const [scope, setScopeState] = useState<SiteScope>(() => loadFromStorage());
  const qc = useQueryClient();

  useEffect(() => {
    try {
      if (scope.kind === 'all') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
    } catch {
      // Quota or private-mode — ignore.
    }
    // Invalidate every cached query so list pages re-fetch with the new
    // ?site= filter that apiFetch now injects from localStorage.
    qc.invalidateQueries();
  }, [scope, qc]);

  const setScope = useCallback((s: SiteScope) => setScopeState(s), []);

  const value = useMemo<SiteScopeContextValue>(
    () => ({
      scope,
      setScope,
      isAll: scope.kind === 'all',
      siteId: scope.kind === 'site' ? scope.siteId : null,
      siteCode: scope.kind === 'site' ? scope.siteCode : null,
    }),
    [scope, setScope]
  );

  return <SiteScopeContext.Provider value={value}>{children}</SiteScopeContext.Provider>;
}

export function useSiteScope(): SiteScopeContextValue {
  const ctx = useContext(SiteScopeContext);
  if (!ctx) {
    // Fall through to "all" if a consumer renders outside the provider —
    // safer than throwing during SSR / test contexts.
    return {
      scope: DEFAULT_SCOPE,
      setScope: () => {
        /* noop */
      },
      isAll: true,
      siteId: null,
      siteCode: null,
    };
  }
  return ctx;
}

/**
 * Helper: produce a query-string suffix (including the leading `&` or `?`)
 * for hooks that want to embed the current site filter into their URL.
 *   const qs = siteScopeToQS(scope, 'siteId');  // '&siteId=00000...'
 */
export function siteScopeToQS(scope: SiteScope, key: string = 'site'): string {
  if (scope.kind === 'all') return '';
  return `&${key}=${encodeURIComponent(scope.siteId)}`;
}
